import React, { useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    Tooltip
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import BookmarkIcon from '@material-ui/icons/Bookmark'

// NPPES code → human label. Codes per the CMS CodeValues spec (§1.4 Sex, §1.2 Sole Proprietor).
const SEX_LABELS = { M: 'Male', F: 'Female', U: 'Undisclosed', X: 'Undisclosed' }
const SOLE_PROP_LABELS = { X: 'Not Answered', Y: 'Yes', N: 'No' }
const labelOr = (map, code) => (code ? map[code] || code : '—')

const readIdentity = (entity) => {
    const basic = entity?.basic || {}
    const addresses = entity?.addresses || []
    const taxonomies = entity?.taxonomies || []
    const primaryTaxonomy = taxonomies.find(t => t.primary) || taxonomies[0] || {}
    const primaryAddress = addresses.find(a => a.address_purpose === 'LOCATION')
        || addresses.find(a => a.address_purpose === 'MAILING')
        || addresses[0]
        || {}

    const entityType = entity?.enumeration_type || entity?.enumerationType || null
    const isOrg = entityType === 'NPI-2'
    const displayName = isOrg
        ? (basic.organization_name || basic.organizationName || '(unnamed org)')
        : [basic.first_name || basic.firstName, basic.middle_name || basic.middleName, basic.last_name || basic.lastName]
            .filter(Boolean).join(' ').trim() || '(unnamed)'

    return {
        displayName,
        npi: entity?.number || null,
        entityType,
        state: primaryAddress.state || null,
        city: primaryAddress.city || null,
        taxonomyCode: primaryTaxonomy.code || null,
        taxonomyDesc: primaryTaxonomy.desc || null,
        taxonomyState: primaryTaxonomy.state || null,
        license: primaryTaxonomy.license || null,
        addresses,
        taxonomies,
    }
}

const readBasic = (entity) => {
    const b = entity?.basic || {}
    return {
        status: b.status || null,
        credential: b.credential || null,
        enumerationDate: b.enumeration_date || b.enumerationDate || null,
        lastUpdated: b.last_updated || b.lastUpdated || null,
        soleProprietor: b.sole_proprietor || b.soleProprietor || null,
        sex: b.sex || null,
        deactivationDate: b.deactivation_date || b.deactivationDate || null,
        deactivationReason: b.deactivation_reason_code || b.deactivationReason || null,
        authorizedOfficialFirstName: b.authorized_official_first_name || null,
        authorizedOfficialLastName: b.authorized_official_last_name || null,
        authorizedOfficialTitle: b.authorized_official_title_or_position || null,
    }
}

const statusBadge = (status) => {
    if (!status) return null
    const style = {
        marginLeft: 6, padding: '1px 6px', fontSize: 10,
        borderRadius: 3, fontWeight: 600, verticalAlign: 'middle'
    }
    if (status === 'D') {
        return <span style={{ ...style, background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }} title='Deactivated'>DEACTIVATED</span>
    }
    return null
}

const ExpandableRow = ({ entity, index, onSave, isSaved, saveEnabled }) => {
    const [open, setOpen] = useState(false)
    const meta = readIdentity(entity)
    const basic = readBasic(entity)

    const savedTooltip = isSaved ? 'Already saved to this application' : 'Save this match to the application'
    const disabledTooltip = 'Only available inside an application'

    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>
                    {meta.displayName}
                    {statusBadge(basic.status)}
                </TableCell>
                <TableCell>{meta.entityType || '—'}</TableCell>
                <TableCell>{basic.credential || '—'}</TableCell>
                <TableCell>{meta.taxonomyDesc || '—'}</TableCell>
                <TableCell>{meta.state || '—'}</TableCell>
                <TableCell>{meta.npi || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    {onSave && (
                        <Tooltip title={saveEnabled ? savedTooltip : disabledTooltip} arrow>
                            <span>
                                <IconButton
                                    size='small'
                                    disabled={!saveEnabled || isSaved}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (!isSaved) onSave({ meta, basic, entity })
                                    }}
                                    aria-label={isSaved ? 'saved' : 'save'}
                                    id={`nppes-save-${index}`}
                                >
                                    {isSaved ? <BookmarkIcon fontSize='small' color='primary'/> : <BookmarkBorderIcon fontSize='small'/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        id={`nppes-toggle-${index}`}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div><strong>NPI:</strong> {meta.npi || '—'} &nbsp;&nbsp; <strong>Entity Type:</strong> {meta.entityType || '—'}</div>
                            <div><strong>Status:</strong> {basic.status || '—'} &nbsp;&nbsp; <strong>Enumeration:</strong> {basic.enumerationDate || '—'} &nbsp;&nbsp; <strong>Last Updated:</strong> {basic.lastUpdated || '—'}</div>
                            {basic.deactivationDate && (
                                <div><strong>Deactivation:</strong> {basic.deactivationDate} {basic.deactivationReason ? `(${basic.deactivationReason})` : ''}</div>
                            )}
                            {meta.entityType === 'NPI-1' && (
                                <div><strong>Sex:</strong> {labelOr(SEX_LABELS, basic.sex)} &nbsp;&nbsp; <strong>Sole Proprietor:</strong> {labelOr(SOLE_PROP_LABELS, basic.soleProprietor)}</div>
                            )}
                            {meta.entityType === 'NPI-2' && basic.authorizedOfficialLastName && (
                                <div><strong>Authorized Official:</strong> {basic.authorizedOfficialFirstName} {basic.authorizedOfficialLastName} {basic.authorizedOfficialTitle ? `(${basic.authorizedOfficialTitle})` : ''}</div>
                            )}
                            <div><strong>Primary Taxonomy:</strong> {meta.taxonomyDesc || '—'} {meta.taxonomyCode ? `[${meta.taxonomyCode}]` : ''}</div>
                            {meta.license && (
                                <div><strong>License:</strong> {meta.license} {meta.taxonomyState ? `(${meta.taxonomyState})` : ''}</div>
                            )}
                            {meta.addresses.length > 0 && (
                                <div style={{ marginTop: 6 }}>
                                    <strong>Addresses:</strong>
                                    <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                        {meta.addresses.map((a, idx) => (
                                            <li key={idx}>
                                                <em>{a.address_purpose || 'ADDRESS'}:</em>{' '}
                                                {[a.address_1, a.address_2, a.city, a.state, a.postal_code, a.country_code].filter(Boolean).join(', ')}
                                                {a.telephone_number ? ` · ${a.telephone_number}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const NppesResultsTable = ({ entities, onSave, savedKeys = new Set(), saveEnabled = false }) => {
    return (
        <TableContainer component={Paper} className='mt-3'>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Credential</TableCell>
                        <TableCell>Primary Taxonomy</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>NPI</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entities.map((entity, i) => {
                        const meta = readIdentity(entity)
                        return (
                            <ExpandableRow
                                key={meta.npi || i}
                                entity={entity}
                                index={i}
                                onSave={onSave}
                                isSaved={savedKeys.has(meta.npi)}
                                saveEnabled={saveEnabled}
                            />
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default NppesResultsTable
